import { lazy, Suspense, useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from '@/components/ProtectedRoute';
import LegacyAuthRedirect from '@/components/LegacyAuthRedirect';
import PreserveQueryRedirect from '@/components/PreserveQueryRedirect';
import useSpaPageTracking from '@/hooks/useSpaPageTracking';

// Eager — landing page + shared layout (small, needed on first paint)
import MarketingLayout from '@/components/marketing/MarketingLayout';
import Home from '@/pages/Home';

// Route-based code splitting — every non-landing page is its own chunk so
// the initial bundle only carries what Home needs. Heavy libs (recharts,
// react-quill, three.js/cobe, react-leaflet, @hello-pangea/dnd, jspdf) now
// load only on the routes that use them.
const HowItWorks = lazy(() => import('@/pages/HowItWorks'));
const GuaranteedPlacements = lazy(() => import('@/pages/GuaranteedPlacements'));
const OutletDetail = lazy(() => import('@/pages/OutletDetail'));
const JournalistOutreach = lazy(() => import('@/pages/JournalistOutreach'));
const NewswireDistribution = lazy(() => import('@/pages/NewswireDistribution'));
const FindYourFit = lazy(() => import('@/pages/FindYourFit'));
const ReputationManagement = lazy(() => import('@/pages/ReputationManagement'));
const ServicesOverview = lazy(() => import('@/pages/ServicesOverview'));
const ResourcesOverview = lazy(() => import('@/pages/ResourcesOverview'));
const Packages = lazy(() => import('@/pages/Packages'));
const Mena = lazy(() => import('@/pages/Mena'));
const Uae = lazy(() => import('@/pages/Uae'));
const Saudi = lazy(() => import('@/pages/Saudi'));
const GulfNewswire = lazy(() => import('@/pages/GulfNewswire'));
const Uk = lazy(() => import('@/pages/Uk'));
const UkPackages = lazy(() => import('@/pages/UkPackages'));
const Usa = lazy(() => import('@/pages/Usa'));
const Fintech = lazy(() => import('@/pages/Fintech'));
const Startup = lazy(() => import('@/pages/Startup'));
const Crypto = lazy(() => import('@/pages/Crypto'));
const GuestPosting = lazy(() => import('@/pages/GuestPosting'));
const Resellers = lazy(() => import('@/pages/Resellers'));
const ResellerApply = lazy(() => import('@/pages/ResellerApply'));
const MediaNetwork = lazy(() => import('@/pages/MediaNetwork'));
const About = lazy(() => import('@/pages/About'));
const Newsroom = lazy(() => import('@/pages/Newsroom'));
const PressReleaseDetail = lazy(() => import('@/pages/PressReleaseDetail'));
const Guides = lazy(() => import('@/pages/Guides'));
const GuideDetail = lazy(() => import('@/pages/GuideDetail'));
const CaseStudies = lazy(() => import('@/pages/CaseStudies'));
const CaseStudyDetail = lazy(() => import('@/pages/case-studies/CaseStudyDetail'));
const EditorialCalendar = lazy(() => import('@/pages/EditorialCalendar'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const HelpCentre = lazy(() => import('@/pages/HelpCentre'));
const Contact = lazy(() => import('@/pages/Contact'));
const Terms = lazy(() => import('@/pages/Terms'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('@/pages/RefundPolicy'));

// Auth pages
const PostLoginRedirect = lazy(() => import('@/components/PostLoginRedirect'));
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));

// Dashboard pages
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const OrderDetail = lazy(() => import('@/pages/OrderDetail'));
const NewOrder = lazy(() => import('@/pages/NewOrder'));
const OrderConfirmation = lazy(() => import('@/pages/OrderConfirmation'));
const AccountSettings = lazy(() => import('@/pages/AccountSettings'));
const Profile = lazy(() => import('@/pages/Profile'));
const Invoices = lazy(() => import('@/pages/Invoices'));
const CustomerNotifications = lazy(() => import('@/pages/CustomerNotifications'));
const CompleteOrderContent = lazy(() => import('@/pages/CompleteOrderContent'));

// Admin pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminOrderDetail = lazy(() => import('@/pages/admin/AdminOrderDetail'));
const AdminProducts = lazy(() => import('@/pages/admin/AdminProducts'));
const AdminMarketingPackages = lazy(() => import('@/pages/admin/AdminMarketingPackages'));
const AdminPromoCodes = lazy(() => import('@/pages/admin/AdminPromoCodes'));
const AdminGridManager = lazy(() => import('@/pages/admin/AdminGridManager'));
const AdminCustomers = lazy(() => import('@/pages/admin/AdminCustomers'));
const AdminCustomerDetail = lazy(() => import('@/pages/admin/AdminCustomerDetail'));
const AdminResellers = lazy(() => import('@/pages/admin/AdminResellers'));
const AdminResellerDetail = lazy(() => import('@/pages/admin/AdminResellerDetail'));
const AdminNotifications = lazy(() => import('@/pages/admin/AdminNotifications'));
const AdminNewsroom = lazy(() => import('@/pages/admin/AdminNewsroom'));
const AdminPageMeta = lazy(() => import('@/pages/admin/AdminPageMeta'));
const AdminErrorLogs = lazy(() => import('@/pages/admin/AdminErrorLogs'));
const AdminInvoices = lazy(() => import('@/pages/admin/AdminInvoices'));
const AdminTeam = lazy(() => import('@/pages/admin/AdminTeam'));
const AdminPdfUpload = lazy(() => import('@/pages/admin/AdminPdfUpload'));

// Redirects an unauthenticated user to /login, preserving the originally
// requested path + query in the ?next= param so login can return them there.
const RedirectToLogin = () => {
  const location = useLocation();
  const next = location.pathname + location.search;
  return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
};

const PageSpinner = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-ink">
    <div className="w-8 h-8 border-4 border-cobalt/20 border-t-cobalt rounded-full animate-spin"></div>
  </div>
);

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();
  useSpaPageTracking();

  // Redirect unauthenticated users to /login — but only when they're NOT
  // already on an auth route.  Without this guard the login page itself
  // re-triggers auth_required → redirects to /login again → infinite loop
  // (ERR_TOO_MANY_REDIRECTS).  ProtectedRoute handles gating protected
  // routes individually.
  const AUTH_ROUTE_PREFIXES = ['/login', '/register', '/forgot-password', '/reset-password', '/post-login'];
  const currentPath = window.location.pathname;
  const isAuthRoute = AUTH_ROUTE_PREFIXES.some((p) => currentPath === p || currentPath.startsWith(p + '/'));

  useEffect(() => {
    if (authError?.type === 'auth_required' && !isAuthRoute) {
      navigateToLogin();
    }
  }, [authError, navigateToLogin, isAuthRoute]);

  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required' && !isAuthRoute) {
      return <PageSpinner />;
    }
  }

  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        {/* Auth */}
        <Route path="/accounts/login" element={<LegacyAuthRedirect to="/login" />} />
        <Route path="/accounts/signup" element={<LegacyAuthRedirect to="/register" />} />
        <Route path="/accounts/onboarding" element={<LegacyAuthRedirect to="/register" />} />
        <Route path="/request-demo" element={<LegacyAuthRedirect to="https://calendly.com/saadzaidi-digital24/30min" />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/post-login" element={<PostLoginRedirect />} />

        {/* Marketing — public */}
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<Navigate to="/packages" replace />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/services/guaranteed-placements" element={<GuaranteedPlacements />} />
          <Route path="/outlets/:slug" element={<OutletDetail />} />
          <Route path="/outlets" element={<PreserveQueryRedirect to="/media-network" />} />
          <Route path="/outlets/" element={<PreserveQueryRedirect to="/media-network" />} />
          <Route path="/services/newswire-syndication" element={<PreserveQueryRedirect to="/services/newswire-distribution" />} />
          <Route path="/services/newswire-syndication/" element={<PreserveQueryRedirect to="/services/newswire-distribution" />} />
          <Route path="/services/journalist-outreach" element={<JournalistOutreach />} />
          <Route path="/services/newswire-distribution" element={<NewswireDistribution />} />
          <Route path="/services/find-your-fit" element={<FindYourFit />} />
          <Route path="/services/reputation-management" element={<ReputationManagement />} />
          <Route path="/services" element={<ServicesOverview />} />
          <Route path="/resources" element={<ResourcesOverview />} />
          <Route path="/packages" element={<Packages />} />
          <Route path="/packages/uk" element={<UkPackages />} />
          <Route path="/mena" element={<Mena />} />
          <Route path="/uae" element={<Uae />} />
          <Route path="/saudi" element={<Saudi />} />
          <Route path="/gulf-newswire" element={<GulfNewswire />} />
          <Route path="/uk" element={<Uk />} />
          <Route path="/usa" element={<Usa />} />
          <Route path="/fintech" element={<Fintech />} />
          <Route path="/startup" element={<Startup />} />
          <Route path="/crypto" element={<Crypto />} />
          <Route path="/editorial-placements" element={<Navigate to="/services/guaranteed-placements" replace />} />
          <Route path="/reputation-management" element={<Navigate to="/services/reputation-management" replace />} />
          <Route path="/newswire" element={<Navigate to="/services/newswire-distribution" replace />} />
          <Route path="/services/targeted-journalist-distribution" element={<Navigate to="/services/journalist-outreach" replace />} />
          <Route path="/services/guaranteed-media-placements" element={<Navigate to="/services/guaranteed-placements" replace />} />
          <Route path="/services/personal-brand-strategy" element={<Navigate to="/services/find-your-fit" replace />} />
          <Route path="/why-digital24" element={<PreserveQueryRedirect to="/about" />} />
          <Route path="/why-digital" element={<PreserveQueryRedirect to="/about" />} />
          <Route path="/pr-agency" element={<PreserveQueryRedirect to="/resellers" />} />
          <Route path="/marketing-team" element={<Navigate to="/how-it-works" replace />} />
          <Route path="/business-owner" element={<Navigate to="/how-it-works" replace />} />
          <Route path="/help-and-support" element={<Navigate to="/help" replace />} />
          <Route path="/help-centre" element={<PreserveQueryRedirect to="/help" />} />
          <Route path="/help-centre/" element={<PreserveQueryRedirect to="/help" />} />
          <Route path="/help-centre/uploading-and-editing" element={<PreserveQueryRedirect to="/help" />} />
          <Route path="/help-centre/uploading-and-editing/" element={<PreserveQueryRedirect to="/help" />} />
          <Route path="/pr-freelancer" element={<PreserveQueryRedirect to="/resellers" />} />
          <Route path="/pr-freelancer/" element={<PreserveQueryRedirect to="/resellers" />} />
          <Route path="/services/seo-guest-posting" element={<PreserveQueryRedirect to="/services/guaranteed-placements" />} />
          <Route path="/services/seo-guest-posting/" element={<PreserveQueryRedirect to="/services/guaranteed-placements" />} />
          <Route path="/gulf-news" element={<PreserveQueryRedirect to="/outlets/gulf-news" />} />
          <Route path="/gulf-news/" element={<PreserveQueryRedirect to="/outlets/gulf-news" />} />
          <Route path="/gulfnews" element={<PreserveQueryRedirect to="/outlets/gulf-news" />} />
          <Route path="/gulfnews/" element={<PreserveQueryRedirect to="/outlets/gulf-news" />} />
          <Route path="/mena-bundle" element={<PreserveQueryRedirect to="/mena" />} />
          <Route path="/mena-bundle/" element={<PreserveQueryRedirect to="/mena" />} />
          {/* Legacy /bulk-purchase -> Resellers (force fresh build: D24-redirect-redeploy) */}
          <Route path="/bulk-purchase" element={<PreserveQueryRedirect to="/resellers" />} />
          <Route path="/bulk-purchase/" element={<PreserveQueryRedirect to="/resellers" />} />
          <Route path="/request-package" element={<PreserveQueryRedirect to="/contact" />} />
          <Route path="/request-package/" element={<PreserveQueryRedirect to="/contact" />} />
          <Route path="/bulk-purchases" element={<Navigate to="/resellers" replace />} />
          <Route path="/uk-bundle" element={<PreserveQueryRedirect to="/uk" />} />
          <Route path="/uk-bundle/" element={<PreserveQueryRedirect to="/uk" />} />
          <Route path="/entrepreneur" element={<PreserveQueryRedirect to="/" />} />
          <Route path="/entrepreneur/" element={<PreserveQueryRedirect to="/" />} />
          <Route path="/offers" element={<PreserveQueryRedirect to="/packages" />} />
          <Route path="/offers/" element={<PreserveQueryRedirect to="/packages" />} />
          <Route path="/campaigns" element={<PreserveQueryRedirect to="/" />} />
          <Route path="/campaigns/*" element={<PreserveQueryRedirect to="/" />} />
          {/* Legacy campaign-creation flow → current order creation page */}
          <Route path="/create-campaign/channels" element={<PreserveQueryRedirect to="/dashboard/new-order" />} />
          <Route path="/guest-posting" element={<GuestPosting />} />
          <Route path="/resellers" element={<Resellers />} />
          <Route path="/resellers/apply" element={<ResellerApply />} />
          <Route path="/media-network" element={<MediaNetwork />} />
          <Route path="/about" element={<About />} />
          <Route path="/news" element={<Navigate to="/newsroom" replace />} />
          <Route path="/news/:id" element={<PressReleaseDetail />} />
          <Route path="/newsroom" element={<Newsroom />} />
          <Route path="/newsroom/:id" element={<PressReleaseDetail />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/guides/:slug" element={<GuideDetail />} />
          <Route path="/guides/ace-money-transfer-visa-coverage-report" element={<Navigate to="/case-studies/ace-money-transfer" replace />} />
          <Route path="/certainty-framework" element={<Navigate to="/guides/certainty-framework" replace />} />
          <Route path="/the-certainty-framework" element={<Navigate to="/guides/certainty-framework" replace />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
          <Route path="/editorial-calendar" element={<EditorialCalendar />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/help" element={<HelpCentre />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
        </Route>

        {/* Protected — Customer Dashboard */}
        <Route element={<ProtectedRoute unauthenticatedElement={<RedirectToLogin />} />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/orders/:orderId" element={<OrderDetail />} />
          <Route path="/dashboard/orders/:orderId/complete-content" element={<CompleteOrderContent />} />
          <Route path="/dashboard/new-order" element={<NewOrder />} />
          <Route path="/dashboard/order-confirmation" element={<OrderConfirmation />} />
          <Route path="/dashboard/invoices" element={<Invoices />} />
          <Route path="/dashboard/notifications" element={<CustomerNotifications />} />
          <Route path="/dashboard/account" element={<AccountSettings />} />
          <Route path="/dashboard/profile" element={<Profile />} />
        </Route>

        {/* Protected — Admin (admin + super_admin) */}
        <Route element={<ProtectedRoute unauthenticatedElement={<RedirectToLogin />} requiredRoles={["admin", "super_admin"]} />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/products" element={<AdminProducts />} />
          <Route path="/admin/marketing-packages" element={<AdminMarketingPackages />} />
          <Route path="/admin/promo-codes" element={<AdminPromoCodes />} />
          <Route path="/admin/grid" element={<AdminGridManager />} />
          <Route path="/admin/orders/:orderId" element={<AdminOrderDetail />} />
          <Route path="/admin/notifications" element={<AdminNotifications />} />
          <Route path="/admin/newsroom" element={<AdminNewsroom />} />
          <Route path="/admin/page-meta" element={<AdminPageMeta />} />
          <Route path="/admin/error-logs" element={<AdminErrorLogs />} />
          <Route path="/admin/invoices" element={<AdminInvoices />} />
          <Route path="/admin/pdf-upload" element={<AdminPdfUpload />} />
        </Route>

        {/* Protected — Admin + Super Admin (customer management) */}
        <Route element={<ProtectedRoute unauthenticatedElement={<RedirectToLogin />} requiredRoles={["admin", "super_admin"]} />}>
          <Route path="/admin/customers" element={<AdminCustomers />} />
          <Route path="/admin/customers/:userId" element={<AdminCustomerDetail />} />
          <Route path="/admin/resellers" element={<AdminResellers />} />
          <Route path="/admin/resellers/:userId" element={<AdminResellerDetail />} />
        </Route>

        {/* Protected — Super Admin only (team management) */}
        <Route element={<ProtectedRoute unauthenticatedElement={<RedirectToLogin />} requiredRoles={["super_admin"]} />}>
          <Route path="/admin/team" element={<AdminTeam />} />
        </Route>

        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <ScrollToTop />
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App